"use client";

import { useEffect } from "react";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { useFieldArray, useFormContext } from "react-hook-form";

import { RSVPStepsFormInputData } from "../validators";
import Linkify from "linkify-react";
import { If } from "~/components/v3/Conditional";
import { Label } from "~/components/v3/shadcn/Label";

type TermsItem = NonNullable<EventBySlugOutput["community"]>["terms"][number];

type Props = {
    communityTerms: {
        title?: string;
        description?: string;
        terms: TermsItem[];
    };
    setNextDisabled: (disabled: boolean) => void;
};

const TermsAndConditionsModalContent = ({
    communityTerms,
    setNextDisabled,
}: Props) => {
    const { title, description, terms } = communityTerms;
    const {
        register,
        formState: { errors, isValid },
        control,
        trigger,
    } = useFormContext<RSVPStepsFormInputData>();

    const { fields } = useFieldArray({
        control,
        name: "terms",
        rules: {
            minLength: terms.length,
        },
    });

    useEffect(() => {
        if (!terms) return;

        setNextDisabled(!isValid);
    }, [isValid, setNextDisabled, terms]);

    return (
        <div className="flex flex-col justify-start gap-4 text-start">
            <div className="flex flex-col gap-2 border-b border-v3-N400 pb-4 text-left text-[16px]">
                <Label className="text-white">{title}</Label>
                <p>{description}</p>
                <div className="flex flex-col justify-start text-start">
                    {fields.map((field, index) => (
                        <div
                            key={field.id}
                            className="mt-4 flex flex-row items-center gap-4"
                        >
                            <span className="gap-2 overflow-hidden break-words">
                                <Linkify
                                    options={{
                                        className:
                                            "text-wrap text-v2-foreground hover:underline",
                                        target: "_blank",
                                    }}
                                >
                                    {field.text}
                                    <If condition={!!field.isRequired}>
                                        <span className="align-super text-sm text-v3-R500">
                                            {" "}
                                            (*required)
                                        </span>
                                    </If>
                                </Linkify>
                            </span>
                            <input
                                type="checkbox"
                                {...register(`terms.${index}.checked`, {
                                    required: terms[index]?.isRequired
                                        ? "This term is required"
                                        : false,
                                })}
                                onBlur={() => trigger(`terms.${index}.checked`)}
                                id={`term-${index}`}
                                className="w-full"
                            />
                        </div>
                    ))}
                </div>
            </div>
            {errors.terms && (
                <div className="text-sm text-red-500">
                    Please accept all required terms and conditions.
                </div>
            )}
        </div>
    );
};

export default TermsAndConditionsModalContent;
